import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';

import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { hydrateGuestFavorites, selectFavorites } from '../../ducks/favorites.duck';

const RESULT_PAGE_SIZE = 100;

// ================ Selectors ================ //

/**
 * Returns the fetched favorite listings, filtered to those still
 * present in the user's favorites list. Without this filter, removing
 * a favorite from this page would leave its card on screen until
 * loadData runs again — the SDK results are cached in `currentPageResultIds`.
 */
export const getFavoriteListings = state => {
  const fetchedIds = state.FavoriteListingsPage.currentPageResultIds;
  const favoriteIds = selectFavorites(state);
  const stillFavorited = fetchedIds.filter(id => favoriteIds.includes(id.uuid));
  return getListingsById(state, stillFavorited);
};

// ================ Async Thunks ================ //

const queryFavoritesPayloadCreator = (
  { favoriteIds, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  if (!favoriteIds || favoriteIds.length === 0) {
    return Promise.resolve({ data: { data: [] } });
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      ids: favoriteIds,
      perPage: RESULT_PAGE_SIZE,
      // Don't show sold items. A sold listing stays `published` (it's just
      // stock 0), so without this it would keep showing here even though it
      // can't be bought. `stockMode: 'match-undefined'` keeps listing types
      // that don't track stock — same pattern as SearchPage. Note this only
      // affects what's fetched: the listing stays in the user's saved
      // favorites, so it reappears if the seller restocks it.
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['author', 'author.profileImage', 'images'],
      'fields.image': [
        // Uncropped variants — ListingCard prefers these so the card crop is
        // centred by CSS rather than imgix's off-centre edge detection.
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        // Author avatar variants — see ListingCard <Avatar />.
        'variants.square-xsmall',
        'variants.square-xsmall2x',
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryFavoriteListingsThunk = createAsyncThunk(
  'app/FavoriteListingsPage/queryFavorites',
  queryFavoritesPayloadCreator
);

// ================ Slice ================ //

const initialState = {
  currentPageResultIds: [],
  queryInProgress: false,
  queryListingsError: null,
};

const resultIds = data =>
  (data?.data || [])
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);

const favoriteListingsPageSlice = createSlice({
  name: 'FavoriteListingsPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(queryFavoriteListingsThunk.pending, state => {
        state.queryInProgress = true;
        state.queryListingsError = null;
        state.currentPageResultIds = [];
      })
      .addCase(queryFavoriteListingsThunk.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload?.data);
        state.queryInProgress = false;
      })
      .addCase(queryFavoriteListingsThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryListingsError = action.payload;
      });
  },
});

export default favoriteListingsPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState) => {
  // Refresh the guest cache from localStorage every time the page
  // loads — covers tabs that toggled favorites elsewhere and the
  // first-load case after a hard refresh.
  dispatch(hydrateGuestFavorites());

  const runQuery = () => {
    const favoriteIds = selectFavorites(getState());
    return dispatch(queryFavoriteListingsThunk({ favoriteIds, config }));
  };

  // `fetchCurrentUser()` is a no-op for unauthenticated visitors, so
  // we still call it — it lets the page display the user's saved
  // favorites when signed in, and falls back to the guest cache (via
  // `selectFavorites`) otherwise.
  const isAuthenticated = !!getState()?.auth?.isAuthenticated;
  if (isAuthenticated) {
    return dispatch(fetchCurrentUser()).then(runQuery);
  }
  return runQuery();
};
